import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const MONTH_NAMES_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_NAMES_UA = ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];
const DAY_NAMES_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_NAMES_UA = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];
function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year: number, month: number) {
  const day = new Date(year, month, 1).getDay();
  // Convert Sunday=0 to Monday-based (Mon=0, Sun=6)
  return day === 0 ? 6 : day - 1;
}
export function JoinCluster() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const {
    t,
    language
  } = useLanguage();
  const now = new Date();
  const [currentMonth, setCurrentMonth] = useState(now.getMonth());
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const benefits = ['join.b1', 'join.b2', 'join.b3', 'join.b4'];
  const times = ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'];
  const monthNames = language === 'ua' ? MONTH_NAMES_UA : MONTH_NAMES_EN;
  const dayNames = language === 'ua' ? DAY_NAMES_UA : DAY_NAMES_EN;
  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const dates = Array.from({
    length: daysInMonth
  }, (_, i) => i + 1);
  const goToPrevMonth = () => {
    // Don't go before current month
    const nowMonth = now.getMonth();
    const nowYear = now.getFullYear();
    if (currentYear === nowYear && currentMonth === nowMonth) return;
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
    setSelectedDate(null);
    setSelectedTime(null);
  };
  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
    setSelectedDate(null);
    setSelectedTime(null);
  };
  const isPastDate = (date: number) => {
    const check = new Date(currentYear, currentMonth, date);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return check < today;
  };
  const isToday = (date: number) => {
    return date === now.getDate() && currentMonth === now.getMonth() && currentYear === now.getFullYear();
  };
  const canGoPrev = !(currentYear === now.getFullYear() && currentMonth === now.getMonth());
  const handleConfirm = () => {
    if (selectedDate && selectedTime) {
      setIsConfirmed(true);
    }
  };
  return <section id="join" ref={ref} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
    backgroundColor: 'var(--bg-primary)'
  }} data-id="element-225">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none" data-id="element-226">
        <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
      }} data-id="element-227" />
      </div>

      <div className="relative z-10 mx-auto max-w-6xl" data-id="element-228">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-229">
          {/* Left Side: Content */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-230">
            <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight" style={{
            color: 'var(--text-primary)'
          }} data-id="element-231">
              {t('join.title')}
            </h2>
            <p className="text-lg mb-10" style={{
            color: 'var(--text-secondary)'
          }} data-id="element-232">
              {t('join.desc')}
            </p>

            <ul className="space-y-4" data-id="element-233">
              {benefits.map((benefitKey, index) => <li key={index} className="flex items-start gap-3" data-id="element-234">
                  <CheckCircleIcon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{
                color: 'var(--accent)'
              }} data-id="element-235" />
                  <span className="text-base font-medium" style={{
                color: 'var(--text-primary)'
              }} data-id="element-236">
                    {t(benefitKey)}
                  </span>
                </li>)}
            </ul>
          </motion.div>

          {/* Right Side: Calendar Widget */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-237">
            <div className="rounded-3xl border p-6 sm:p-8 shadow-xl" style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--bg-primary)'
          }} data-id="element-238">
              {isConfirmed ? <div className="flex flex-col items-center justify-center text-center py-12" data-id="element-239">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{
                backgroundColor: 'rgba(16, 185, 129, 0.1)'
              }} data-id="element-240">
                    <CheckIcon className="w-8 h-8 text-emerald-500" data-id="element-241" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4" style={{
                color: 'var(--text-primary)'
              }} data-id="element-242">
                    {t('join.cal.success')}
                  </h3>
                  <p style={{
                color: 'var(--text-secondary)'
              }} data-id="element-243">
                    {selectedDate} {monthNames[currentMonth]} {currentYear},{' '}
                    {selectedTime}
                  </p>
                </div> : <>
                  <h3 className="text-2xl font-bold mb-6" style={{
                color: 'var(--text-primary)'
              }} data-id="element-244">
                    {t('join.cal.title')}
                  </h3>

                  {/* Calendar Header */}
                  <div className="flex items-center justify-between mb-6" data-id="element-245">
                    <span className="font-bold text-lg" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-246">
                      {monthNames[currentMonth]} {currentYear}
                    </span>
                    <div className="flex gap-2" data-id="element-247">
                      <button onClick={goToPrevMonth} disabled={!canGoPrev} className="p-2 rounded-lg border transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-30 disabled:cursor-not-allowed" style={{
                    borderColor: 'var(--border)',
                    color: 'var(--text-primary)'
                  }} data-id="element-248">
                        <ChevronLeftIcon className="w-4 h-4" data-id="element-249" />
                      </button>
                      <button onClick={goToNextMonth} className="p-2 rounded-lg border transition-colors hover:bg-[var(--bg-tertiary)]" style={{
                    borderColor: 'var(--border)',
                    color: 'var(--text-primary)'
                  }} data-id="element-250">
                        <ChevronRightIcon className="w-4 h-4" data-id="element-251" />
                      </button>
                    </div>
                  </div>

                  {/* Calendar Grid */}
                  <div className="mb-8" data-id="element-252">
                    <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-253">
                      {dayNames.map(day => <div key={day} className="text-center text-xs font-medium py-2" style={{
                    color: 'var(--text-muted)'
                  }} data-id="element-254">
                          {day}
                        </div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1" data-id="element-255">
                      {/* Empty cells for first day offset */}
                      {Array.from({
                    length: firstDay
                  }).map((_, i) => <div key={`empty-${i}`} className="p-2" data-id="element-256" />)}
                      {dates.map(date => {
                    const isSelected = selectedDate === date;
                    const past = isPastDate(date);
                    const today = isToday(date);
                    return <button key={date} onClick={() => {
                      if (!past) {
                        setSelectedDate(date);
                        setSelectedTime(null);
                      }
                    }} disabled={past} className={`p-2 w-full aspect-square rounded-full flex items-center justify-center text-sm font-medium transition-colors ${past ? 'opacity-30 cursor-not-allowed' : isSelected ? 'text-white' : 'hover:bg-[var(--bg-tertiary)]'}`} style={{
                      backgroundColor: isSelected ? 'var(--accent)' : 'transparent',
                      color: isSelected ? '#FFFFFF' : 'var(--text-primary)',
                      ...(today && !isSelected ? {
                        boxShadow: 'inset 0 0 0 2px var(--accent)'
                      } : {})
                    }} data-id="element-257">
                            {date}
                          </button>;
                  })}
                    </div>
                  </div>

                  {/* Time Slots */}
                  {selectedDate && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} className="border-t pt-6" style={{
                borderColor: 'var(--border)'
              }} data-id="element-258">
                      <h4 className="text-sm font-bold mb-4" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-259">
                        {t('join.cal.time')}
                      </h4>
                      <div className="grid grid-cols-3 gap-3 mb-6" data-id="element-260">
                        {times.map(time => {
                    const isSelected = selectedTime === time;
                    return <button key={time} onClick={() => setSelectedTime(time)} className="py-2 rounded-lg border text-sm font-medium transition-colors" style={{
                      borderColor: isSelected ? 'var(--accent)' : 'var(--border)',
                      backgroundColor: isSelected ? 'var(--accent)' : 'var(--bg-tertiary)',
                      color: isSelected ? '#FFFFFF' : 'var(--text-primary)'
                    }} data-id="element-261">
                              {time}
                            </button>;
                  })}
                      </div>

                      {selectedTime && <button onClick={handleConfirm} className="w-full py-3.5 rounded-xl font-bold text-white transition-transform hover:scale-[1.02]" style={{
                  backgroundColor: 'var(--accent)'
                }} data-id="element-262">
                          {t('join.cal.confirm')}
                        </button>}
                    </motion.div>}
                </>}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}